import type { SessionSide, VersionPin, VersionSummary } from '@matter_extractor/shared';

/**
 * The words of a reference, `latest` or a precise version (vitrine.md §3.1,
 * V3, V16), and the few facts a screen derives from the pair of a pin and the
 * versions of the object it points at.
 *
 * PURE, like the other `*-labels` and `*-draft` modules: the menu, the signal
 * « vN disponible », the sentences of the save box and the link rows all read
 * the same two or three answers, and a second spelling of one of them would
 * show up as a pill that says « v6 » beside a signal that says « v5 ».
 */

/** The pill's word for a pin: `latest`, or `vN`. */
export function pinWord(pin: VersionPin): string {
  return pin === 'latest' ? 'latest' : `v${pin}`;
}

/**
 * « latest v11 » or « v6 » - a pin with the version it RESOLVES to, for the
 * sentences that name a fact about one version: « Le protocole « PCQ » (latest
 * v11) n'a pas de lecture pour l'analyste ». A pinned version says its number
 * once.
 */
export function pinResolvedWord(pin: VersionPin, versionNumber: number): string {
  return pin === 'latest' ? `latest v${versionNumber}` : `v${versionNumber}`;
}

/** The word of a side of a session or a link entry (V18): « dev » or « prod ». */
export const SIDE_WORD: Readonly<Record<SessionSide, string>> = {
  dev: 'dev',
  prod: 'prod',
};

/**
 * The version `latest` plays on the atelier for the editor that holds the
 * object's development pointer: « latest · v6 sur le dev » is what the pill
 * says beside the word (revue du 2026-10-14).
 */
export function latestPlays(developmentNumber: number): string {
  return `v${developmentNumber} sur le dev`;
}

/**
 * Whether a pinned reference is behind the latest dev version of what it
 * points at - the condition of the signal « vN disponible - ce protocole
 * épingle vM » (§3.3). `latest` is never behind: it follows.
 */
export function pinIsBehind(pin: VersionPin, developmentNumber: number): boolean {
  return pin !== 'latest' && pin < developmentNumber;
}

/** The versions of an object, most recent first - the order the menu lists them in. */
export function newestFirst(versions: readonly VersionSummary[]): VersionSummary[] {
  return [...versions].sort((a, b) => b.number - a.number);
}

/** The pin a menu choice means: the string `latest`, or the number the option carries. */
export function pinOfChoice(choice: string): VersionPin | null {
  if (choice === 'latest') return 'latest';
  const number = Number(choice);
  return Number.isInteger(number) && number > 0 ? number : null;
}
